import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../AppIcon';
import GlobalHeader from './GlobalHeader';
import { useAuth } from '../../auth/AuthProvider';
import useTranslation from '../../hooks/useTranslation';
import { supabase } from '../../lib/supabase';
import { projectService } from '../../services/projectService';
import { isAdmin, canSeeAllProjects } from '../../utils/permissions';
import { usePagePermissions } from '../../utils/pagePermissions';
import organizationService from '../../services/organizationService';
import useBrainAccess from '../../hooks/useBrainAccess';
import brandIcon from '../../assets/brand/onsitepm-icon.svg';
import brandLogo from '../../assets/brand/onsitepm-logo-blanc.svg';

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { user, userProfile } = useAuth();
  const { t } = useTranslation();
  const [inboxCount, setInboxCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [orgInfo, setOrgInfo] = useState(null);

  // Sidebar collapse (icons-only rail) — persisted per browser.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('onsitepm.sidebarCollapsed') === 'true'; } catch { return false; }
  });
  const toggleCollapse = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('onsitepm.sidebarCollapsed', String(next)); } catch { /* ignore */ }
    return next;
  });

  const isProjectRoute = location?.pathname?.startsWith('/project/');
  const projectId = params?.id;

  // Fetch current organization info
  useEffect(() => {
    const fetchOrgInfo = async () => {
      const orgId = userProfile?.organization_id;
      if (!orgId) return;
      const { data } = await organizationService?.getOrgInfo(orgId);
      if (data) setOrgInfo(data);
    };
    fetchOrgInfo();
  }, [userProfile?.organization_id]);

  useEffect(() => {
    const fetchInboxCount = async () => {
      const { count } = await supabase?.from('invoice_inbox')?.select('*', { count: 'exact', head: true })?.in('status', ['new', 'ready']);
      setInboxCount(count || 0);
    };

    fetchInboxCount();

    const channel = supabase
      ?.channel('invoice_inbox_count')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'invoice_inbox' }, fetchInboxCount)
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, []);

  useEffect(() => {
    const fetchProjectCount = async () => {
      const count = await projectService?.getVisibleProjectCount();
      setProjectCount(count);
    };

    fetchProjectCount();

    const channel = supabase
      ?.channel('projects_count')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, fetchProjectCount)
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, []);

  const { canUseBrain } = useBrainAccess();
  const { ready: pagesReady, canAccessPage } = usePagePermissions();

  // Chaque entree porte sa cle de gabarit. Les conditions de ROLE existantes
  // restent : le gabarit s'ajoute, il ne remplace rien. Une entree s'affiche
  // seulement si le role l'autorise ET si le gabarit ouvre la page.
  const dashboardNavItems = [
    { path: '/project-detail', label: t('sidebar.projectList'), icon: 'FolderKanban', count: projectCount, pageKey: 'app.project_list' },
    ...(isAdmin(userProfile) ? [{ path: '/schedule', label: t('sidebar.projectSchedule'), icon: 'Calendar', pageKey: 'app.schedule' }] : []),
    { path: '/directory', label: t('sidebar.directory'), icon: 'Users', pageKey: 'app.directory' },
    { path: '/invoice-inbox', label: t('sidebar.invoiceInbox'), icon: 'Inbox', count: inboxCount, pageKey: 'app.invoice_inbox' },
    { path: '/team-schedule', label: t('sidebar.teamSchedule'), icon: 'CalendarDays', pageKey: 'app.team_schedule' },
    ...(canSeeAllProjects(userProfile) ? [
      { path: '/vue-projet', label: t('sidebar.projectView'), icon: 'LayoutDashboard', pageKey: 'app.project_view' },
    ] : []),
    // Brain : reserve aux admins dont l'organisation a le plan. La reponse
    // vient de la base, jamais d'un drapeau local.
    ...(isAdmin(userProfile) && canUseBrain ? [
      { path: '/brain', label: t('sidebar.brain'), icon: 'BrainCircuit', pageKey: 'app.brain' },
    ] : []),
    // OnX : chat IA plein ecran, reserve aux admins.
    ...(isAdmin(userProfile) ? [
      { path: '/onx', label: t('sidebar.onx'), icon: 'MessageSquareText', pageKey: 'app.onx' },
    ] : []),
  ];

  // Ordre metier : on remonte le terrain (repertoire, documents, calendrier,
  // journal, QRT) avant l'argent (budget, contrats, extras, avenants, depenses,
  // factures), et on ferme par les quittances. Aucune entree n'est
  // conditionnelle : les 14 s'affichent toujours, dans cet ordre.
  const projectNavItems = [
    { path: `/project-detail`, label: t('sidebar.backToProjectList'), icon: 'ArrowLeft' },
    { path: `/project/${projectId}/directory`, label: t('projectDirectory.sidebarLabel'), icon: 'Users' , pageKey: 'project.directory' },
    { path: `/project/${projectId}/documents`, label: t('sidebar.documents'), icon: 'FolderOpen' , pageKey: 'project.documents' },
    { path: `/project/${projectId}/schedule`, label: t('sidebar.schedule'), icon: 'Calendar' , pageKey: 'project.schedule' },
    { path: `/project/${projectId}/daily-log`, label: t('sidebar.dailyLog'), icon: 'ClipboardList' , pageKey: 'project.daily_log' },
    { path: `/project/${projectId}/rfi`, label: t('sidebar.rfi'), icon: 'MessageSquare' , pageKey: 'project.rfi' },
    { path: `/project/${projectId}/budget`, label: t('sidebar.construction_cost'), icon: 'DollarSign' , pageKey: 'project.budget' },
    { path: `/project/${projectId}/contracts`, label: t('sidebar.contracts'), icon: 'Users' , pageKey: 'project.contracts' },
    { path: `/project/${projectId}/extras`, label: t('sidebar.extrasCredits'), icon: 'Plus' , pageKey: 'project.extras' },
    { path: `/project/${projectId}/change-orders`, label: t('sidebar.changeOrders'), icon: 'GitBranch' , pageKey: 'project.change_orders' },
    { path: `/project/${projectId}/expenses`, label: t('sidebar.expenses'), icon: 'Receipt' , pageKey: 'project.expenses' },
    { path: `/project/${projectId}/sub-invoices`, label: t('sidebar.subInvoicing'), icon: 'FileText' , pageKey: 'project.sub_invoices' },
    { path: `/project/${projectId}/client-invoices`, label: t('sidebar.clientInvoicing'), icon: 'Receipt' , pageKey: 'project.client_invoices' },
    { path: `/project/${projectId}/quittances`, label: t('quittances.sidebarLabel'), icon: 'ShieldCheck' , pageKey: 'project.quittances' }
  ];

  // Tant que la reponse du gabarit n'est pas la, on n'affiche AUCUNE entree :
  // mieux vaut un menu vide une fraction de seconde qu'une entree qui
  // disparait sous le curseur.
  const allNavItems = isProjectRoute ? projectNavItems : dashboardNavItems;
  const navItems = pagesReady ? allNavItems?.filter((i) => canAccessPage(i?.pageKey)) : [];

  // Motif de refus depose par RequirePage au moment de la redirection.
  const deniedPage = location?.state?.deniedPage || null;

  const orgName = orgInfo?.name || '';

  // Depuis que la racine est h-screen overflow-hidden, c'est <main> qui defile,
  // plus la fenetre. ScrollToTop (monte hors de ce layout) ne fait qu'un
  // window.scrollTo(0,0) — sans effet ici. On remet donc <main> en haut a
  // chaque changement de route. window.scrollTo reste, inoffensif, pour les
  // pages rendues HORS AppLayout (login, reset, 404) qui defilent la fenetre.
  const mainRef = useRef(null);
  useEffect(() => {
    if (mainRef?.current) mainRef.current.scrollTop = 0;
  }, [location?.pathname]);

  return (
    <div className="h-screen overflow-hidden bg-[#162637] flex">
      <aside className={`${collapsed ? 'w-16' : 'w-56'} bg-[#162637] border-r border-[#1E2F44] h-screen flex flex-col flex-shrink-0 transition-[width] duration-200`}>
        {/* Brand + collapse toggle */}
        <div className={`px-3 pt-4 pb-3 flex ${collapsed ? 'flex-col items-center gap-2' : 'items-center gap-2.5'}`}>
          <img src={brandIcon} alt={collapsed ? 'OnSitePM' : ''} aria-hidden={collapsed ? undefined : true} draggable={false} className="block w-8 h-8 flex-shrink-0 select-none" />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="leading-none">
                <img src={brandLogo} alt="OnSitePM" draggable={false} className="block h-[17px] w-auto select-none" />
              </h1>
            </div>
          )}
          <button
            type="button"
            onClick={toggleCollapse}
            className="p-1.5 rounded-md text-[#9FB3C8] hover:bg-[#1E2F44] hover:text-[#E6EDF3] transition-colors flex-shrink-0"
          >
            <Icon name={collapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={18} />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 pt-1 px-3 pb-3 space-y-1 overflow-y-auto overflow-x-hidden">
          {navItems?.map((item) => {
            const isActive = item?.path === '/project-detail' ? location?.pathname?.startsWith('/project-detail')
              : item?.path === '/vue-projet' ? location?.pathname?.startsWith('/vue-projet')
              : location?.pathname === item?.path;
            return (
              <Link
                key={item?.path}
                to={item?.path}
                title={collapsed ? item?.label : undefined}
                className={
                  isActive
                    ? "w-full flex items-center gap-2.5 py-2.5 rounded-lg transition-colors text-left font-medium text-sm text-primary"
                    : "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-left text-[#9FB3C8] text-sm hover:bg-[#1E2F44] hover:text-[#E6EDF3]"
                }
                style={
                  isActive
                    ? { backgroundColor: '#2A3F58', borderLeft: '3px solid #FF6B2C', paddingLeft: '9px', paddingRight: '12px' }
                    : undefined
                }
              >
                <Icon name={item?.icon} size={20} className="flex-shrink-0" />
                {!collapsed && <span className="flex-1 truncate">{item?.label}</span>}
                {!collapsed && item?.count > 0 && (
                  <span className={isActive ? "text-xs font-semibold text-primary" : "text-xs font-semibold text-[#9FB3C8]"}>
                    {item?.count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        {/* Footer — profil de l'utilisateur et son menu (ouvert vers le haut) */}
        <div className={`border-t border-[#1E2F44] py-3 flex-shrink-0 ${collapsed ? 'px-2' : 'px-3'}`}>
          <GlobalHeader collapsed={collapsed} />
        </div>
      </aside>

      {/* Main column — rounded card sitting over the navy */}
      <div className="flex-1 min-h-0 flex flex-col bg-background rounded-tl-2xl overflow-hidden">
        <main ref={mainRef} className="flex-1 min-h-0 overflow-auto overscroll-contain">
          <div className="pt-4 pb-6 px-6 h-full">
            {/* Refus d'acces : le message est rendu ICI, a l'arrivee de la
                redirection. Sans lui, l'utilisateur reviendrait a la liste
                des projets sans jamais savoir pourquoi. */}
            {deniedPage && (
              <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <Icon name="Lock" size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">{t('common.accessDenied.banner')}</p>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;